import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type HTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type Ref,
} from 'react'
import { cx, isFilled } from '@/shared/lib'
import { Divider } from '@/shared/ui/Divider'
import { SectionCaption } from '@/shared/ui/SectionCaption'
import s from './Menu.module.css'

/**
 * `danger` — необратимое действие («Удалить», «Выйти»);
 * `accent` — действие-ссылка внутри меню («Сбросить ширины и колонки», «Показать ещё 50»).
 */
export type MenuItemTone = 'default' | 'danger' | 'accent'

type MenuItemCommon = {
  /** Ключ отрисовки, адрес пункта в `onSelect(id)` и якорь теста (`data-menu-item`). */
  id: string
  /** Подпись пункта. Узел допустим: подсветка совпадения в результатах палитры. */
  label: ReactNode
  /** Текст для набора первых букв, когда `label` — не строка. */
  textValue?: string
  /** Вторая строка пункта: «Заказ 14-882 · 1 284 500 ₽». Одна строка с многоточием. */
  description?: ReactNode
  /** Слот перед подписью. Декоративен: смысл несёт подпись. */
  icon?: ReactNode
  /** Хвост строки: `<Kbd />`, «текущий», «нет прав», остаток на складе. */
  meta?: ReactNode
  tone?: MenuItemTone
  /** Нет права или действие неприменимо. Пункт остаётся в дереве доступности выключенным. */
  disabled?: boolean
  /** «Вы сейчас здесь»: текущий раздел, текущая категория среди соседей крошки. */
  current?: boolean
}

/** Служебные пропсы пункта для слота `render`: раскладываются на свой элемент как есть. */
export type MenuItemSlotProps = {
  className: string
  children: ReactNode
  role: 'menuitem'
  tabIndex: number
  'aria-current': 'true' | undefined
  'data-menu-item': string
  onClick: () => void
  onFocus: () => void
}

export type MenuActionItem = MenuItemCommon & {
  kind?: 'action'
  /** Действие пункта. Закрытие панели — забота `onSelect` меню. */
  onSelect?: () => void
  /** Пункт-переход: `<a href>` со средним кликом и «открыть в новой вкладке». */
  href?: string
  /** Пункт-переход роутера: элемент создаёт потребитель — примитив о роутере не знает. */
  render?: (props: MenuItemSlotProps) => ReactNode
}

export type MenuCheckboxItem = MenuItemCommon & {
  kind: 'checkbox'
  checked: boolean
  /** Переключение флажка. Меню при этом остаётся открытым. */
  onCheckedChange?: (checked: boolean) => void
}

export type MenuRadioItem = MenuItemCommon & {
  kind: 'radio'
  checked: boolean
  onSelect?: () => void
}

export type MenuSeparatorItem = {
  kind: 'separator'
  id?: string
}

export type MenuLeafItem = MenuActionItem | MenuCheckboxItem | MenuRadioItem

export type MenuGroupItem = {
  kind: 'group'
  id: string
  /** Подпись группы **в обычном регистре**: заглавные ставит CSS. Даёт группе имя. */
  label: string
  items: MenuLeafItem[]
}

export type MenuItem = MenuLeafItem | MenuSeparatorItem | MenuGroupItem

export type MenuProps = {
  /** Строки меню в порядке отрисовки. Ровно то, что рисуем: ни сортировки, ни фильтрации внутри. */
  items: MenuItem[]
  /** Доступное имя меню. Видимую подпись рисует потребитель. */
  label: string
  /** Пункт сработал: сюда вешают закрытие панели. У `checkbox` не зовётся — меню остаётся открытым. */
  onSelect?: (id: string) => void
  /** Фокус на первый доступный пункт при монтировании. Ставится здесь, а не у `Popover`. */
  autoFocus?: boolean
  /** Список пуст: «Ничего не нашлось по «DL360»». Слот пуст и список пуст — не рендерится ничего. */
  empty?: ReactNode
  /** Корень с `role="menu"`: прокрутка к выбранному пункту, измерение. */
  ref?: Ref<HTMLDivElement>
} & Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'onSelect' | 'role'>

/** Буфер набора первых букв. APG рекомендует ~500 мс: короче — рвётся слово, длиннее — залипает. */
const TYPEAHEAD_RESET_MS = 500

// CSS-модуль типизирован индексной сигнатурой, поэтому значения — string | undefined
const TONE_CLASS: Record<MenuItemTone, string | undefined> = {
  default: undefined,
  danger: s.toneDanger,
  accent: s.toneAccent,
}

/** Пункт, до которого доходит фокус: выключенные в обход не попадают. */
type NavigableItem = { id: string; text: string }

const typeaheadText = (item: MenuLeafItem): string =>
  (item.textValue ?? (typeof item.label === 'string' ? item.label : '')).trim().toLowerCase()

function collectNavigable(items: MenuItem[]): NavigableItem[] {
  const result: NavigableItem[] = []

  const push = (item: MenuLeafItem) => {
    if (item.disabled === true) return
    result.push({ id: item.id, text: typeaheadText(item) })
  }

  for (const item of items) {
    if (item.kind === 'separator') continue
    if (item.kind === 'group') {
      for (const leaf of item.items) push(leaf)
      continue
    }
    push(item)
  }

  return result
}

/**
 * Список команд над тем, что уже показано: кебаб строки реестра, меню аккаунта, селектор
 * ценового сегмента, видимость колонок таблицы, сгруппированные результаты палитры ⌘K.
 *
 * Рисует **строки, а не панель**: стекло, тень, позиционирование, Esc и клик вне приходят от
 * `Popover`, а в потоке страницы — от `Surface`. Так `Select`, `MultiSelect`, `Combobox` и
 * `Breadcrumbs` переиспользуют одни и те же строки в разных контейнерах.
 *
 * Клавиатура по APG: стрелки с зацикливанием, Home/End, набор первых букв, Enter/Space,
 * пропуск выключенных пунктов. Фокус ходит по пунктам, поэтому roving `tabIndex` обязателен:
 * в порядке обхода документа меню занимает ровно одну позицию.
 *
 * Контракт: docs/ui-kit/contracts/menu.md
 */
export function Menu({
  items,
  label,
  onSelect,
  autoFocus = false,
  empty,
  className,
  onKeyDown,
  ref,
  ...rest
}: MenuProps) {
  const reactId = useId()
  const [activeId, setActiveId] = useState<string | null>(null)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const typeahead = useRef({ query: '', at: 0 })

  /** Корень нужен и снаружи (потребителю), и внутри (поиск пункта под фокус): ref раздваивается. */
  const attachRoot = useCallback(
    (node: HTMLDivElement | null) => {
      rootRef.current = node
      // ref-колбэк потребителя в React 19 вправе вернуть cleanup — его нельзя терять
      if (typeof ref === 'function') return ref(node)
      if (ref) ref.current = node
      return undefined
    },
    [ref],
  )

  const navigable = collectNavigable(items)
  // Пункт мог исчезнуть вместе с ответом сервера — ноль в этом случае возвращается на первый
  const tabbableId = navigable.some((item) => item.id === activeId) ? activeId : navigable[0]?.id

  useEffect(() => {
    if (!autoFocus) return

    // Ноль в порядке обхода стоит ровно на том пункте, с которого начинается меню
    rootRef.current
      ?.querySelector<HTMLElement>('[data-menu-item][tabindex="0"]')
      ?.focus({ preventScroll: true })
  }, [autoFocus])

  /*
   * Узел пункта ищется в DOM, а не хранится картой ref: карта заводилась бы прямо в разметке,
   * а ref во время отрисовки читать нельзя. Пунктов в меню единицы — обход дешевле счётчика.
   */
  const focusItem = (id: string | undefined) => {
    if (id === undefined) return

    setActiveId(id)
    const nodes = rootRef.current?.querySelectorAll<HTMLElement>('[data-menu-item]') ?? []
    for (const node of nodes) {
      if (node.dataset.menuItem === id) {
        node.focus({ preventScroll: true })
        return
      }
    }
  }

  const moveFocus = (delta: 1 | -1) => {
    if (navigable.length === 0) return

    const current = navigable.findIndex((item) => item.id === tabbableId)
    focusItem(navigable[(current + delta + navigable.length) % navigable.length]?.id)
  }

  const focusByTypeahead = (char: string) => {
    if (navigable.length === 0) return

    const now = Date.now()
    const fresh = now - typeahead.current.at <= TYPEAHEAD_RESET_MS
    const query = (fresh ? typeahead.current.query : '') + char.toLowerCase()
    typeahead.current = { query, at: now }

    /*
     * Первая буква ищет следующий пункт (повтор буквы перебирает однобуквенные совпадения),
     * продолжение набора — начиная с текущего: иначе «ко» убегало бы с «Колонки» на «Копия».
     */
    const from = navigable.findIndex((item) => item.id === tabbableId)
    const offset = query.length > 1 ? 0 : 1

    for (let step = 0; step < navigable.length; step += 1) {
      const candidate = navigable[(from + offset + step) % navigable.length]
      if (candidate?.text.startsWith(query) === true) {
        focusItem(candidate.id)
        return
      }
    }
  }

  /** Enter и Space там, где браузер не справляется сам: ссылка и произвольный узел из `render`. */
  const activateFocused = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const focused =
      event.target instanceof HTMLElement
        ? event.target.closest<HTMLElement>('[data-menu-item]')
        : null
    if (focused === null) return

    // Кнопку браузер нажмёт сам обеими клавишами, ссылку — по Enter: второй клик был бы лишним
    if (focused.tagName === 'BUTTON') return
    if (focused.tagName === 'A' && event.key === 'Enter') return

    event.preventDefault()
    focused.click()
  }

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event)
    if (event.defaultPrevented) return

    // ⌘⏎ «в новой вкладке» из палитры и системные сочетания меню не перехватывает
    if (event.altKey || event.ctrlKey || event.metaKey) return

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        moveFocus(1)
        return
      case 'ArrowUp':
        event.preventDefault()
        moveFocus(-1)
        return
      case 'Home':
        event.preventDefault()
        focusItem(navigable[0]?.id)
        return
      case 'End':
        event.preventDefault()
        focusItem(navigable[navigable.length - 1]?.id)
        return
      default:
        break
    }

    // Пробел посреди набора — часть искомой фразы («меню кол…»), а не нажатие пункта
    const typing =
      typeahead.current.query !== '' && Date.now() - typeahead.current.at <= TYPEAHEAD_RESET_MS

    if ((event.key === 'Enter' || event.key === ' ') && !(event.key === ' ' && typing)) {
      activateFocused(event)
      return
    }

    if (event.key.length !== 1) return

    // Пробел иначе прокрутит панель под меню или нажмёт пункт, на котором стоит фокус
    if (event.key === ' ') event.preventDefault()
    focusByTypeahead(event.key)
  }

  const activate = (item: MenuLeafItem) => {
    if (item.kind === 'checkbox') {
      item.onCheckedChange?.(!item.checked)
      return
    }

    item.onSelect?.()
    onSelect?.(item.id)
  }

  const renderLeaf = (item: MenuLeafItem) => {
    const checked = item.kind === 'checkbox' || item.kind === 'radio' ? item.checked : undefined
    const role =
      item.kind === 'checkbox'
        ? 'menuitemcheckbox'
        : item.kind === 'radio'
          ? 'menuitemradio'
          : 'menuitem'

    const itemClassName = cx(
      s.item,
      TONE_CLASS[item.tone ?? 'default'],
      item.current === true && s.current,
      item.kind === 'radio' && item.checked && s.selected,
      item.disabled === true && s.disabled,
    )

    const content = (
      <>
        {item.kind === 'checkbox' ? (
          <span className={cx(s.tick, item.checked && s.tickChecked)} aria-hidden="true" />
        ) : null}

        {isFilled(item.icon) ? (
          <span className={s.icon} aria-hidden="true">
            {item.icon}
          </span>
        ) : null}

        <span className={s.text}>
          <span className={s.label}>{item.label}</span>
          {/* Пробел перед второй строкой — не оформление: без него доступное имя пункта
              склеивается в «Заказ 14-882Собран, ожидает отгрузки». Во flex-раскладке
              текстовый узел из одних пробелов отбрасывается и на вид не влияет. */}
          {isFilled(item.description) ? (
            <>
              {' '}
              <span className={s.description}>{item.description}</span>
            </>
          ) : null}
        </span>

        {/* Мета остаётся в дереве доступности: «нет прав», остаток и ускоритель нужны на слух */}
        {isFilled(item.meta) ? (
          <>
            {' '}
            <span className={s.meta}>{item.meta}</span>
          </>
        ) : null}
      </>
    )

    /*
     * Выключенный пункт — не кнопка: так он не берёт фокус, не притворяется нажимаемым
     * и при этом остаётся в дереве доступности выключенным (правило 8).
     */
    if (item.disabled === true) {
      return (
        <span
          key={item.id}
          className={itemClassName}
          role={role}
          aria-checked={checked}
          aria-current={item.current === true ? 'true' : undefined}
          aria-disabled="true"
          data-menu-item={item.id}
        >
          {content}
        </span>
      )
    }

    const base = {
      className: itemClassName,
      tabIndex: tabbableId === item.id ? 0 : -1,
      'aria-current': item.current === true ? ('true' as const) : undefined,
      'data-menu-item': item.id,
      onClick: () => activate(item),
      // Клик мышью переносит фокус мимо стрелок — roving обязан узнать об этом
      onFocus: () => setActiveId(item.id),
    }

    if (item.kind === undefined || item.kind === 'action') {
      if (item.render !== undefined) {
        return (
          <Fragment key={item.id}>
            {item.render({ ...base, role: 'menuitem', children: content })}
          </Fragment>
        )
      }

      if (item.href !== undefined) {
        return (
          <a key={item.id} {...base} role="menuitem" href={item.href}>
            {content}
          </a>
        )
      }
    }

    return (
      <button key={item.id} type="button" {...base} role={role} aria-checked={checked}>
        {content}
      </button>
    )
  }

  if (items.length === 0) {
    // Пустой список меню не объявляет: объявлять нечего, а фокусировать тем более
    if (!isFilled(empty)) return null

    return (
      <div {...rest} ref={attachRoot} className={cx(s.root, s.empty, className)}>
        {empty}
      </div>
    )
  }

  return (
    <div
      {...rest}
      ref={attachRoot}
      className={cx(s.root, className)}
      role="menu"
      aria-label={label}
      onKeyDown={handleKeyDown}
    >
      {items.map((item, index) => {
        if (item.kind === 'separator') {
          return (
            <Divider key={item.id ?? `separator-${index}`} tone="subtle" spacing="xs" inset="sm" />
          )
        }

        if (item.kind === 'group') {
          const captionId = `${reactId}-${item.id}`

          return (
            <div key={item.id} className={s.group} role="group" aria-labelledby={captionId}>
              <SectionCaption className={s.caption} labelId={captionId}>
                {item.label}
              </SectionCaption>
              {item.items.map(renderLeaf)}
            </div>
          )
        }

        return renderLeaf(item)
      })}
    </div>
  )
}
